import React, { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SerializedStyles } from "@emotion/react";
import { Button, Text } from "@epignosis_llc/gnosis";
import { CourseExitSVG } from "@epignosis_llc/gnosis/icons";

import { courseHeaderMobileStyles } from "@components/CourseOverview/components/CourseHeader/styles";

import { getCourseCategoryText } from "@components/CourseOverview/components/CourseHeader/helpers";
import { useApplyTranslations } from "@hooks";

import CoverImage from "@components/CourseOverview/components/CourseHeader/CoverImage";

import { Course, CourseCategory, Section } from "types/entities";

type CourseHeaderMobileProps = {
  course: Course;
  categoryTree: CourseCategory[];
  sections: Section[];
  isCatalogCourseView?: boolean;
  backLink?: string;
};

const CourseHeaderMobile: FC<CourseHeaderMobileProps> = ({ course, categoryTree, backLink }) => {
  const navigate = useNavigate();
  const { t } = useApplyTranslations();

  const { name, category, code, cover_image } = course;
  const { categoryText } = getCourseCategoryText({ category, categoryTree });

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const courseNameElement = document.getElementById("course-name");

    const checkVisibility = (): void => {
      const rect = courseNameElement?.getBoundingClientRect();
      if (rect) {
        const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
        setIsVisible(isVisible);
      }
    };

    checkVisibility(); // Check on mount
    window.addEventListener("scroll", checkVisibility); // Check on scroll or resize
    window.addEventListener("resize", checkVisibility);

    return () => {
      window.removeEventListener("scroll", checkVisibility);
      window.removeEventListener("resize", checkVisibility);
    };
  }, []);

  return (
    <>
      <div css={(theme): SerializedStyles => courseHeaderMobileStyles(theme)}>
        {backLink && (
          <div className="back-button-container">
            <Button
              noGutters
              color="primaryLight"
              className="back-button"
              aria-label={t("a11y.button.navigateBack")}
              onClick={(): void => navigate(backLink)}
            >
              <CourseExitSVG height={32} />
            </Button>

            {!isVisible && (
              <Text fontSize={"sm"} as="div" weight="700" className="course-name">
                {name}
              </Text>
            )}
          </div>
        )}

        <div className="mobile-title">
          <Text fontSize={"sm"} className="category">
            {categoryText}
          </Text>

          <Text as="div" fontSize={"lg"} weight="700" id="course-name">
            {name}
          </Text>

          {code && (
            <Text fontSize={"sm"} className="code">
              {code}
            </Text>
          )}
        </div>

        <div className="mobile-cover-image">
          <CoverImage coverImage={cover_image} courseName={name} />
        </div>
      </div>
    </>
  );
};

export default CourseHeaderMobile;
